import React from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import moment from "moment";
import classNames from "classnames";
import { get } from "lodash";
import {
  getStatus,
  getTableBatteryInfo,
  getUnavailabilities,
  getPSAtypical,
  getPSGeneric,
  changeTab
} from "../actions";
import TableContent from '../components/operationData/table-content';
import unavailabilitiesSelector from "../selectors/get_unavailabilities";
import batteryStatusSelector from "../selectors/get_battery_status";
import genericPsSelector from "../selectors/get_generic_ps";
import atypicalPsSelector from "../selectors/get_atypical_ps";

//Material UI
import { withStyles } from "@material-ui/core/styles";
import Typography from "@material-ui/core/Typography";
import CircularProgress from "@material-ui/core/CircularProgress";
import Grid from "@material-ui/core/Grid";
import Card from "@material-ui/core/Card";
import CardContent from "@material-ui/core/CardContent";
import DateRange from "@material-ui/icons/DateRange";
import List from "@material-ui/core/List";
import ListItem from "@material-ui/core/ListItem";
import ListItemText from "@material-ui/core/ListItemText";

const styles = theme => ({
  root: {
    padding: 20
  },
  active: {
    backgroundColor: "#005bbb",
    "& span": {
      color: "#ffffff"
    },
    "&:focus": {
      backgroundColor: "#005bbb"
    },
    "&:hover": {
      backgroundColor: "#005bbb"
    }
  },
  disabled: {
    display: 'none',
    opacity: .5
  }
});

const dataSets = [
  {
    title: "Alarms / Events"
  },
  {
    title: "EMS Unavailability",
    disabled: true
  },
  {
    title: "Battery Status",
    disabled: true
  },
  {
    title: "Peak Shaving",
    disabled: true
  }
];

const alarmUpdatedTitle = "Alarms / Events";
const alarmUpdatedColumns = [
  {
    key: "displayTimestamp",
    isNumeric: false,
    padding: "default",
    label: "Date"
  },
  {
    key: "alarmName",
    isNumeric: false,
    padding: "default",
    label: "ID"
  },
  {
    key: "alarmValue",
    isNumeric: false,
    padding: "default",
    label: "Status"
  }
];

class DataViewer extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      selectedIndex: 0,
    };
  }

  getContent = async (startDate, endDate) => {
    await this.props.getStatus(startDate, endDate);
    this.props.getTableBatteryInfo();
    this.props.getUnavailabilities();
    this.props.getPSAtypical();
    this.props.getPSGeneric();
  };

  componentDidMount = async () => {
    const { startDate, endDate } = this.props;
    this.getContent(startDate, endDate);
    this.setContent(this.getTabId());
  };

  componentDidUpdate = (prevProps) => {
    const { selectedIndex } = this.state;
    const currentTabId = this.getTabId();

    if (currentTabId !== selectedIndex) {
      this.setContent(currentTabId);
    }

    const prevStartDate = prevProps.startDate.format();
    const startDate = this.props.startDate.format();
    const prevEndDate = prevProps.endDate.format();
    const endDate = this.props.endDate.format();

    if (prevStartDate !== startDate || prevEndDate !== endDate) {
      const { startDate, endDate } = this.props;
      this.getContent(startDate, endDate);
    }
  };

  getTabId = () => parseInt(get(this.props.match, 'params.tabId', null), 10);

  setContent = (index) => this.setState({ selectedIndex: index });

  handleListItemClick = (event, index) => this.props.changeTab('op-data', index);

  getMainContent = () => {
    const {
      loadingUnavailabilities,
      unavailabilities,
      status,
      loadingStatus,
      tableBattery,
      loadingBatteryStatus,
      isAtypical,
      genericPS,
      atypicalPS,
      loadingAtypicalPs,
      loadingGenericPs
    } = this.props;
    const { selectedIndex } = this.state;

    switch (selectedIndex) {
      case 1 :
        // EMS Unavailabilities
        return <TableContent
          title="EMS Unavailability"
          columns={[
            {
              key: "displayStart",
              isNumeric: false,
              padding: "default",
              label: "Start"
            },
            {
              key: "displayEnd",
              isNumeric: false,
              padding: "default",
              label: "End"
            }
          ]}
          orderBy="displayStart"
          order="desc"
          loading={loadingUnavailabilities}
          updatedData={unavailabilities}
        />;
      case 2 :
        // Battery Unavailability
        return <TableContent
          title="Battery Status"
          columns={[
            {
              key: "displayTimestamp",
              isNumeric: false,
              padding: "default",
              label: "Date"
            },
            {
              key: "displayState",
              isNumeric: false,
              padding: "default",
              label: "State"
            }
          ]}
          orderBy="displayTimestamp"
          order="desc"
          loading={loadingBatteryStatus}
          updatedData={tableBattery}
        />;
      case 3 :
        // Peak Shaving
        const updatedColumns = isAtypical ?
          [
            {
              key: "displayTimestamp",
              isNumeric: false,
              padding: "default",
              label: "Date"
            },
            {
              key: "peak_s",
              isNumeric: false,
              padding: "default",
              label: "Peak Duration Seconds"
            },
            {
              key: "bess_extracted_kwh",
              isNumeric: false,
              padding: "default",
              label: "Max Customer Meter (kW)"
            },
            {
              key: "displaySuccess",
              isNumeric: false,
              padding: "default",
              label: "Maintened Meter Below Pmax"
            },
            {
              key: "customer_p_max_no_bess_kw",
              isNumeric: false,
              padding: "default",
              label: "Max Power Without BESS (kW)"
            },
            {
              key: "customer_peak_max_mn",
              isNumeric: false,
              padding: "default",
              label: "Max Peak Duration (min)"
            },
            {
              key: "customer_peak_max_kwh",
              isNumeric: false,
              padding: "default",
              label: "Max Peak Energy (kWh)"
            },
            {
              key: "customer_deviation",
              isNumeric: false,
              padding: "default",
              label: "Deviation (%)"
            },
            {
              key: "customer_deviation_no_bess",
              isNumeric: false,
              padding: "default",
              label: "Deviation Without BESS (%)"
            }
          ] :
          [
            {
              key: "displayTimestamp",
              isNumeric: false,
              padding: "default",
              label: "Date"
            },
            {
              key: "peak_s",
              isNumeric: false,
              padding: "default",
              label: "Peak Duration Seconds"
            },
            {
              key: "customer_next_p_max_kw",
              isNumeric: false,
              padding: "default",
              label: "Customer Next PMax (kW)"
            },
            {
              key: "bess_extracted_kwh",
              isNumeric: false,
              padding: "default",
              label: "Max Customer Meter (kW)"
            },
            {
              key: "displaySuccess",
              isNumeric: false,
              padding: "default",
              label: "Maintened Meter Below Pmax"
            },
            {
              key: "customer_p_max_no_bess_kw",
              isNumeric: false,
              padding: "default",
              label: "Max Power Without BESS (kW)"
            },
            {
              key: "customer_peak_max_mn",
              isNumeric: false,
              padding: "default",
              label: "Max Peak Duration (min)"
            },
            {
              key: "customer_peak_max_kwh",
              isNumeric: false,
              padding: "default",
              label: "Max Peak Energy (kWh)"
            }
          ];

        return <TableContent
          title="Peak Shaving"
          columns={updatedColumns}
          orderBy="displayTimestamp"
          order="desc"
          loading={isAtypical ? loadingAtypicalPs : loadingGenericPs}
          updatedData={isAtypical ? atypicalPS : genericPS}
        />;
      default :
        // Alarms / Events
        return <TableContent
          title={alarmUpdatedTitle}
          columns={alarmUpdatedColumns}
          orderBy="displayTimestamp"
          order="desc"
          loading={loadingStatus}
          updatedData={status}
        />;
    }
  };

  render() {
    const { classes, loading, loadingAlarms, startDate, endDate } = this.props;
    const displayStart = moment(startDate).format("DD/MM/YYYY");
    const displayEnd = moment(endDate).format("DD/MM/YYYY");


    return <div className={classes.root}>
      <Grid container spacing={3}>
        {/* Sidebar */}
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography gutterBottom variant="h5" style={{ float: "left" }}>
                Available Data
              </Typography>
              {(loading || loadingAlarms) && (
                <CircularProgress
                  disableShrink
                  size={20}
                  style={{ marginLeft: 20 }}
                />
              )}
              <Grid container>
                <Grid item xs={1}>
                  <DateRange fontSize="small"/>
                </Grid>
                <Grid item xs={11}>
                  <Typography variant="caption">
                    {`${displayStart} - ${displayEnd}`}
                  </Typography>
                </Grid>
              </Grid>
              <List component="nav">
                {dataSets.map(({ title: archive, disabled }, index) => (
                  <ListItem
                    key={archive}
                    className={classNames(
                      this.state.selectedIndex === index ? classes.active : "",
                      disabled ? classes.disabled : ''
                    )}
                    button={!disabled}
                    onClick={event => !disabled && this.handleListItemClick(event, index)}
                  >
                    <ListItemText>{archive}</ListItemText>
                  </ListItem>
                ))}
              </List>
            </CardContent>
          </Card>
        </Grid>
        {/* End sidebar */}
        {/* Main content */}
        {this.getMainContent()}
        {/* End main content */}
      </Grid>
    </div>;
  }
}

DataViewer.propTypes = {
  classes: PropTypes.object.isRequired
};

const mapStateToProps = (state) => {
  return {
    genericPS: genericPsSelector(state),
    atypicalPS: atypicalPsSelector(state),
    status: state.statusRange.status,
    headers: state.statusRange.headers,
    startDate: state.tableDateRange.startDate,
    endDate: state.tableDateRange.endDate,
    loading: state.mode.loading,
    loadingAlarms: state.mode.alarms,
    tableBattery: batteryStatusSelector(state),
    loadingUnavailabilities: state.mode.unavailabilities,
    loadingStatus: state.mode.status,
    unavailabilities: unavailabilitiesSelector(state),
    loadingBatteryStatus: state.mode.batteryStatus,
    loadingAtypicalPs: state.mode.atypicalPs,
    loadingGenericPs: state.mode.genericPs,
    isAtypical: state.config.config.ATYPICAL
  };
};

export default withStyles(styles)(
  connect(
    mapStateToProps,
    {
      getStatus,
      getTableBatteryInfo,
      getUnavailabilities,
      getPSAtypical,
      getPSGeneric,
      changeTab
    }
  )(DataViewer)
);



// WEBPACK FOOTER //
// ./src/pages/DataViewer.js